// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import copyToClipboard from 'copy-to-clipboard';
import React, {useState} from 'react';

import {useViewer} from '../../../state/viewer/hooks';
import DocUrls from '../../../util/doc_urls';
import {useIsProjectReadOnly} from '../common/hooks';
import {globalRegistryType} from '../common/utils';

const LOG_ARTIFACT_CODE_SNIPPET = `# create a run and log an artifact to it
with wandb.init(project="my-project") as run:
    artifact = wandb.Artifact(
      name="my-artifact",
      type="my-type"
    )
    artifact.add_file("checkpt.pt")
    run.log_artifact(artifact)
    # logs the version "my-artifact:v0"
`;

enum Quickstart_Section {
  Section1,
  Section2,
  Section3,
  Section2_Code,
}

type QuickstartProps = {
  onCreateCollection: () => void;
  registry: globalRegistryType;
};
export const Quickstart = ({onCreateCollection, registry}: QuickstartProps) => {
  const {entityName, registryProjectName} = registry;
  const {isProjectReadOnly: readOnly} = useIsProjectReadOnly(
    entityName,
    registryProjectName
  );
  const viewer = useViewer();
  const isLoggedIn = !!viewer;

  const [expandedSections, setExpandedSections] = useState<
    Quickstart_Section[]
  >([]);
  const onToggleSectionExpanded = (section: Quickstart_Section) => {
    if (expandedSections.includes(section)) {
      const indexToRemove = expandedSections.indexOf(section);
      setExpandedSections(
        expandedSections.filter((_, i) => i !== indexToRemove)
      );
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };
  const isSectionExpanded = (section: Quickstart_Section) => {
    return expandedSections.includes(section);
  };

  const onCopyLogArtifactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(LOG_ARTIFACT_CODE_SNIPPET);
    toast('Copied to clipboard');
  };
  return (
    <Tailwind style={{width: '502px', borderRadius: '4px'}}>
      <div className="mt-24 flex flex-col justify-center rounded bg-moon-200 p-1">
        <span className="flex items-center gap-8 rounded bg-white px-16 py-12 text-xl leading-8">
          <Icon name="help-alt" />
          FAQs
        </span>
        <div className="h-px bg-moon-200" />
        <div
          className={clsx(
            `${
              isSectionExpanded(Quickstart_Section.Section1)
                ? 'rounded-t'
                : 'rounded'
            }`,
            'min-h-44 cursor-pointer bg-white p-10 hover:bg-moon-50'
          )}
          onClick={() => onToggleSectionExpanded(Quickstart_Section.Section1)}>
          <div className="flex items-center text-base/8 font-semibold">
            <Button
              variant="ghost"
              size="small"
              icon={
                isSectionExpanded(Quickstart_Section.Section1)
                  ? 'chevron-down'
                  : 'chevron-next'
              }
              onClick={() =>
                onToggleSectionExpanded(Quickstart_Section.Section1)
              }
              className="mr-10"
            />
            How do I create my first collection?
          </div>
        </div>
        {isSectionExpanded(Quickstart_Section.Section1) && (
          <>
            <div className="h-px bg-moon-200" />
            <div className="rounded-b bg-white px-40 py-16 text-base font-normal leading-5 text-moon-650">
              A collection represents a task or use-case your team is working on
              and houses a selection of staged artifact versions for this task.
              <div className="mt-16">
                Create a new collection below to start bookmarking your best
                versions to it.
              </div>
              {isLoggedIn && !readOnly && (
                <Button
                  className="create-collection-2 mt-16"
                  variant="secondary"
                  size="medium"
                  onClick={onCreateCollection}>
                  Create collection
                </Button>
              )}
            </div>
          </>
        )}
        <div className="h-px bg-moon-200" />
        <div
          className={clsx(
            `${
              isSectionExpanded(Quickstart_Section.Section2)
                ? 'rounded-t'
                : 'rounded'
            }`,
            'min-h-44 cursor-pointer bg-white p-10 hover:bg-moon-50'
          )}
          onClick={() => onToggleSectionExpanded(Quickstart_Section.Section2)}>
          <div className="flex items-center text-base/8 font-semibold">
            <Button
              variant="ghost"
              size="small"
              icon={
                isSectionExpanded(Quickstart_Section.Section2)
                  ? 'chevron-down'
                  : 'chevron-next'
              }
              onClick={() =>
                onToggleSectionExpanded(Quickstart_Section.Section2)
              }
              className="mr-10"
            />
            How do I log an artifact to an experiment?
          </div>
        </div>
        {isSectionExpanded(Quickstart_Section.Section2) && (
          <>
            <div className="h-px bg-moon-200" />
            <div className="rounded-b bg-white px-40 py-16 text-base font-normal leading-5 text-moon-650">
              When you have a resulting artifact checkpoint that you are ready
              to save during your experiment run, use the code snippet below to
              log it to W&B.
              <div className="mt-16 flex flex-col rounded border border-solid border-moon-250 bg-moon-50 px-8">
                <div className="font-['Source Sans Pro'] flex flex-row items-center py-10 text-sm font-semibold">
                  <Button
                    variant="ghost"
                    size="small"
                    icon={
                      isSectionExpanded(Quickstart_Section.Section2_Code)
                        ? 'chevron-down'
                        : 'chevron-next'
                    }
                    onClick={() =>
                      onToggleSectionExpanded(Quickstart_Section.Section2_Code)
                    }
                    className="mr-4"
                  />
                  Code snippet
                  <div className="ml-auto">
                    <Button
                      icon="copy"
                      variant="ghost"
                      size="small"
                      onClick={onCopyLogArtifactClick}
                      tooltip="Copy to clipboard">
                      Copy
                    </Button>
                  </div>
                </div>
                {isSectionExpanded(Quickstart_Section.Section2_Code) && (
                  <div className="flex-auto whitespace-pre-wrap px-8 pb-8 font-['Source_Code_Pro'] text-xs leading-4">
                    {LOG_ARTIFACT_CODE_SNIPPET}
                  </div>
                )}
              </div>
              <div className="mb-12 mt-16 text-base font-normal leading-5 text-moon-650">
                <TargetBlank href={DocUrls.artifacts}>
                  <span className="cursor-pointer font-semibold text-teal-650 hover:text-teal-500">
                    Learn more
                  </span>
                </TargetBlank>{' '}
                about logging and versioning experiment outputs as W&B Artifacts
              </div>
            </div>
          </>
        )}
        <div className="h-px bg-moon-200" />
        <div
          className={clsx(
            `${
              isSectionExpanded(Quickstart_Section.Section3)
                ? 'rounded-t'
                : 'rounded'
            }`,
            'min-h-44 cursor-pointer bg-white p-10 hover:bg-moon-50'
          )}
          onClick={() => onToggleSectionExpanded(Quickstart_Section.Section3)}>
          <div className="flex items-center text-base/8 font-semibold">
            <Button
              variant="ghost"
              size="small"
              icon={
                isSectionExpanded(Quickstart_Section.Section3)
                  ? 'chevron-down'
                  : 'chevron-next'
              }
              onClick={() =>
                onToggleSectionExpanded(Quickstart_Section.Section3)
              }
              style={{marginRight: '10px'}}
            />
            What are the key features of a Registry?
          </div>
        </div>
        {isSectionExpanded(Quickstart_Section.Section3) && (
          <>
            <div className="h-px bg-moon-200" />
            <div className="rounded-b bg-white px-40 py-16 text-base font-normal leading-5 text-moon-650">
              Use a Registry to bookmark top-performing candidate artifacts
              across an entity
              <ul className="my-16 list-disc">
                <li className="ml-24">
                  Set <span className="font-semibold">artifact stages</span>{' '}
                  such as "candidate" or "production"
                </li>
                <li className="ml-24">
                  <TargetBlank href={DocUrls.registry + 'aliases'}>
                    <span className="cursor-pointer font-semibold text-teal-650 hover:text-teal-500">
                      Handoff artifacts
                    </span>
                  </TargetBlank>{' '}
                  for consumption
                </li>
                <li className="ml-24">
                  Use{' '}
                  <TargetBlank href={DocUrls.automation}>
                    <span className="cursor-pointer font-semibold text-teal-650 hover:text-teal-500">
                      automations
                    </span>
                  </TargetBlank>{' '}
                  to manage CI/CD
                </li>
                <li className="ml-24">
                  Get{' '}
                  <TargetBlank
                    href={DocUrls.automation + 'automation-events/#events'}>
                    <span className="cursor-pointer font-semibold text-teal-650 hover:text-teal-500">
                      alerts for registry events
                    </span>
                  </TargetBlank>{' '}
                  in Slack
                </li>
                <li className="ml-24">
                  Document{' '}
                  <span className="font-semibold">collection cards</span>{' '}
                  centrally
                </li>
              </ul>
              To learn more about these Registry features,{' '}
              <TargetBlank href={DocUrls.registry}>
                <span className="cursor-pointer font-semibold text-teal-650 hover:text-teal-500">
                  click here
                </span>
              </TargetBlank>
              .
            </div>
          </>
        )}
      </div>
    </Tailwind>
  );
};
